import Vue from "vue";
import Router from "vue-router";
import infiniteScroll from "vue-infinite-scroll";
import vueModal from "vue-js-modal";
import vueSplit from "vue-split-panel";
import qs from "friendly-querystring";
import promiseFinally from "promise.prototype.finally";
import Antd from "ant-design-vue";
import {
  Workflow,
  WorkflowHistory,
  WorkflowList,
  WorkflowPending,
} from "@/containers";
import { injectMomentDurationFormat, jsonTryParse } from "@/helpers";
import i18n from "./assets/i18n/translate-message";
import copyButton from "./components/copy";
import snapscroll from "./directives/snapscroll";
import App from "./App";
import Domain from "./routes/domain";
import DomainMetrics from "./routes/domain/domain-metrics";
import DomainSettings from "./routes/domain/domain-settings";
import Query from "./routes/workflow/query";
import StackTrace from "./routes/workflow/stack-trace";
import TaskList from "./routes/task-list";
import TaskListMetrics from "./routes/task-list/metrics";
import TaskListPartition from "./routes/task-list/partition";
import TaskListPollers from "./routes/task-list/pollers";
import WorkflowArchival from "./routes/domain/workflow-archival";
import WorkflowArchivalAdvanced from "./routes/domain/workflow-archival/advanced";
import WorkflowArchivalBasic from "./routes/domain/workflow-archival/basic";
import WorkflowSummary from "./routes/workflow/summary.vue";
import Login from "./components/auth/login";
import SSOLogin from "./components/auth/sso-login";
import WorkflowConfigs from "./routes/workflow-configs/component";

import { FeatureFlag, FlexGrid, FlexGridItem } from "./components";

import initStore from "./store";

Vue.component("feature-flag", FeatureFlag);
Vue.component("flex-grid", FlexGrid);
Vue.component("flex-grid-item", FlexGridItem);

Vue.use(Antd);

const routeOpts = {
  // mode: 'history',
  routes: [
    {
      name: "login",
      path: "/",
      component: Login,
    },
    {
      name: "ssoLogin",
      path: "/ssoLogin",
      component: SSOLogin,
    },
    {
      name: "domain",
      path: "/domains/:domain/:clusterName?",
      redirect: "/domains/:domain/:clusterName?/workflows",
      component: Domain,
      props: ({ params }) => ({
        clusterName: params.clusterName,
        domain: params.domain,
      }),
      children: [
        {
          name: "workflow-list",
          path: "/domains/:domain/:clusterName?/workflows",
          components: {
            "workflow-list": WorkflowList,
          },
        },
        {
          name: "domain-metrics",
          path: "/domains/:domain/:clusterName?/metrics",
          components: {
            "domain-metrics": DomainMetrics,
          },
        },
        {
          name: "domain-settings",
          path: "/domains/:domain/:clusterName?/settings",
          components: {
            "domain-settings": DomainSettings,
          },
        },
        {
          name: "workflow-archival",
          path: "/domains/:domain/:clusterName?/archival",
          redirect: "/domains/:domain/:clusterName?/archival/basic",
          components: {
            "workflow-archival": WorkflowArchival,
          },
          children: [
            {
              name: "workflow-archival-advanced",
              path: "/domains/:domain/:clusterName?/archival/advanced",
              components: {
                "workflow-archival-advanced": WorkflowArchivalAdvanced,
              },
            },
            {
              name: "workflow-archival-basic",
              path: "/domains/:domain/:clusterName?/archival/basic",
              components: {
                "workflow-archival-basic": WorkflowArchivalBasic,
              },
            },
          ],
        },
      ],
    },
    {
      name: "workflow",
      path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId",
      component: Workflow,
      props: ({ params }) => ({
        clusterName: params.clusterName,
        displayWorkflowId: params.workflowId,
        domain: params.domain,
        runId: params.runId,
        workflowId: encodeURIComponent(params.workflowId),
      }),
      children: [
        {
          name: "workflow/summary",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/summary",
          components: {
            summary: WorkflowSummary,
          },
          props: {
            summary: ({ params }) => ({
              clusterName: params.clusterName,
              runId: params.runId,
              workflowId: encodeURIComponent(params.workflowId),
            }),
          },
        },
        {
          name: "workflow/history",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/history",
          components: {
            history: WorkflowHistory,
          },
          props: {
            history: ({ params, query }) => ({
              clusterName: params.clusterName,
              domain: params.domain,
              eventId: Number(query.eventId) || undefined,
              format: query.format || "compact",
              runId: params.runId,
              showGraph: Boolean(query.showGraph) === true,
              graphView: query.graphView,
              workflowId: encodeURIComponent(params.workflowId),
            }),
          },
        },
        {
          name: "workflow/configs",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/configs",
          components: {
            configs: WorkflowConfigs,
          },
          props: {
            configs: ({ params }) => ({
              clusterName: params.clusterName,
              runId: params.runId,
              workflowId: encodeURIComponent(params.workflowId),
            }),
          },
        },
        {
          name: "workflow/pending",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/pending",
          components: {
            pending: WorkflowPending,
          },
          props: {
            pending: ({ params }) => ({
              clusterName: params.clusterName,
            }),
          },
        },
        {
          name: "workflow/stack-trace",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/stack-trace",
          components: {
            stacktrace: StackTrace,
          },
          props: {
            stacktrace: ({ params }) => ({
              clusterName: params.clusterName,
            }),
          },
        },
        {
          name: "workflow/query",
          path: "/domains/:domain/:clusterName?/workflows/:workflowId/:runId/query",
          components: {
            query: Query,
          },
          props: {
            query: ({ params }) => ({
              clusterName: params.clusterName,
            }),
          },
        },
      ],
    },
    {
      name: "task-list",
      path: "/domains/:domain/:clusterName?/task-lists/:taskList",
      redirect: "/domains/:domain/:clusterName?/task-lists/:taskList/pollers",
      component: TaskList,
      props: ({ params }) => ({
        clusterName: params.clusterName,
        domain: params.domain,
        taskList: params.taskList,
      }),
      children: [
        {
          name: "task-list/pollers",
          path: "/domains/:domain/:clusterName?/task-lists/:taskList/pollers",
          components: {
            pollers: TaskListPollers,
          },
        },
        {
          name: "task-list/partition",
          path: "/domains/:domain/:clusterName?/task-lists/:taskList/partition",
          components: {
            partition: TaskListPartition,
          },
        },
        {
          name: "task-list/metrics",
          path: "/domains/:domain/:clusterName?/task-lists/:taskList/metrics",
          components: {
            partition: TaskListMetrics,
          },
        },
      ],
    },

    // redirects

    {
      name: "domains-redirect",
      path: "/domain/*",
      redirect: "/domains/*",
    },
    {
      name: "domain-config-redirect",
      path: "/domains/:domain/:clusterName?/config",
      redirect: "/domains/:domain/:clusterName?/settings",
    },
    {
      path: "/domains/:domain/:clusterName?/history",
      redirect: ({ params, query }) => {
        if (!query.runId || !query.workflowId) {
          return {
            name: "workflow-list",
            params,
          };
        }

        const { runId, workflowId, ...queryWhitelist } = query;

        const newParams = {
          clusterName: params.clusterName,
          runId,
          workflowId,
          domain: params.domain,
        };

        return {
          name: "workflow/history",
          params: newParams,
          query: queryWhitelist,
        };
      },
    },
  ],
  parseQuery: qs.parse.bind(qs),
  stringifyQuery: (query) => {
    const q = qs.stringify(query);

    return q ? `?${q}` : "";
  },
};

const router = new Router(routeOpts);
// 定义全局前置导航守卫
router.beforeEach((to, from, next) => {
  // 处理访问权限
  if (to.name === "login" || to.path.includes("ssoLogin")) {
    // 登录或单点登录
    next();
  } else {
    // 调用接口解析token
    const username = localStorage.getItem("userName");
    if (username && username.length > 0) {
      next();
    } else {
      next("/");
    }
  }
});

Object.getPrototypeOf(router).replaceQueryParam = function replaceQueryParam(
  prop,
  val
) {
  const newQuery = {
    ...this.currentRoute.query,
    [prop]: val,
  };

  if (!newQuery[prop]) {
    delete newQuery[prop];
  }

  this.replace({ query: newQuery });
};

injectMomentDurationFormat();

JSON.tryParse = jsonTryParse;

promiseFinally.shim();

const originalPush = Router.prototype.push;
Router.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => err);
};
Vue.use(infiniteScroll);
Vue.use(vueModal, {
  dialog: true,
  dynamic: true,
});
// Vue.use(vueModal, { dynamic: true, injectModalsContainer: true, dynamicDefaults: { clickToClose: true } });
Vue.use(vueSplit);
Vue.component("copy", copyButton);
Vue.directive("snapscroll", snapscroll);

// Vue.config.ignoredElements = ['loader'];
Vue.use(Router);
if (!document.querySelector("main")) {
  document.body.appendChild(document.createElement("main"));
}

const store = initStore({ router });

new Vue({
  router,
  i18n,
  store,
  render: (h) => h(App),
}).$mount("main");

if (module.hot) {
  // 热更新
  module.hot.addStatusHandler((status) => {
    if (status === "apply") {
      document
        .querySelectorAll("link[href][rel=stylesheet]")
        .forEach((link) => {
          const nextStyleHref = link.href.replace(
            /(\?\d+)?$/,
            `?${Date.now()}`
          );

          // eslint-disable-next-line no-param-reassign
          link.href = nextStyleHref;
        });
    }
  });
}

export default {
  App,
  routeOpts,
};
